import { receiptPlainTextCols, type ReceiptWidthMm } from '@vuelto/print';
import { Platform } from 'react-native';
import * as Print from 'expo-print';

export type PrintHtmlDocumentOptions = {
  /** Texto plano del ticket; en web muestra acciones sin abrir Imprimir solo. */
  plainText?: string;
  /** Web + plainText: abrir diálogo Imprimir al cargar (ticket automático al cobrar). */
  autoOpenPrintDialog?: boolean;
  /** @deprecated Web ya no descarga blob; se conserva por compat de callers. */
  downloadFilename?: string;
  widthMm?: ReceiptWidthMm;
};

const DEFAULT_TICKET_WIDTH_MM: ReceiptWidthMm = 72;
const TOOLBAR_MIN_WIDTH_PX = 340;

function mmToPx(mm: number): number {
  return Math.round(mm * 3.7795275591);
}

function receiptWindowTitle(plainText: string): string {
  for (const rawLine of plainText.split('\n')) {
    const pedido = rawLine.trim().match(/Pedido\s+#(\d+)/i);
    if (pedido?.[1]) return `Ticket · Pedido #${pedido[1]}`;
    const ref = rawLine.trim().match(/Ref\.?\s*(#\w+)/i);
    if (ref?.[1]) return `Ticket · ${ref[1]}`;
    if (/PRECUENTA/i.test(rawLine) || /Resumen del pedido/i.test(rawLine)) {
      return 'Cuenta · Vuelto';
    }
  }
  return 'Ticket · Vuelto';
}

function buttonStyle(primary: boolean): string {
  return [
    'padding:8px 14px',
    'border-radius:8px',
    'border:1px solid #111',
    primary ? 'background:#111;color:#fff' : 'background:#fff;color:#111',
    'font-weight:600',
    'cursor:pointer',
    'font-size:13px',
  ].join(';');
}

/**
 * Screen = toolbar + ticket. Print = solo ticket, mismo CSS tipográfico que el preview.
 * Web: un botón Imprimir → diálogo del browser (térmica o Guardar como PDF en Destino).
 */
function injectPreviewChromeStyles(doc: Document, widthMm: ReceiptWidthMm): void {
  const pageMargin = widthMm === 58 ? 2 : widthMm === 80 ? 3 : 3;
  const cols = receiptPlainTextCols(widthMm);
  const sheetPadScreen =
    widthMm === 58 ? '6px 4px 8px' : widthMm === 80 ? '8px 6px 10px' : '8px 6px 10px';
  const style = doc.createElement('style');
  style.textContent = `
    @page { size: ${widthMm}mm auto; margin: ${pageMargin}mm; }
    @media screen {
      html {
        background: #e5e7eb;
        height: auto;
      }
      body {
        margin: 10px auto;
        padding: 0;
        background: transparent;
        box-shadow: none;
        border-radius: 0;
        width: max(${TOOLBAR_MIN_WIDTH_PX}px, calc(${cols}ch + 28px));
        max-width: calc(100vw - 16px);
        box-sizing: border-box;
      }
      #vuelto-print-toolbar {
        margin: 0 0 14px;
        padding: 10px;
        background: #f3f4f6;
        border: 1px solid #d1d5db;
        border-radius: 10px;
        font-family: system-ui, sans-serif;
        font-size: 13px;
        line-height: 1.45;
        color: #111;
        box-sizing: border-box;
        width: 100%;
      }
      #vuelto-ticket-sheet {
        margin: 0;
        padding: ${sheetPadScreen};
        background: #fff;
        box-shadow: 0 1px 4px rgba(0,0,0,.12);
        border-radius: 4px;
        box-sizing: border-box;
        width: 100%;
      }
      #vuelto-ticket-sheet .line,
      #vuelto-ticket-sheet pre.items {
        margin-left: auto;
        margin-right: auto;
      }
    }
    @media print {
      html, body {
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: auto !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      #vuelto-print-toolbar { display: none !important; }
      #vuelto-ticket-sheet {
        margin: 0 !important;
        padding: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
      }
      #vuelto-ticket-sheet table {
        table-layout: fixed !important;
        width: 100% !important;
      }
      #vuelto-ticket-sheet .title {
        letter-spacing: normal !important;
        word-break: break-word !important;
      }
    }
  `;
  doc.head.appendChild(style);
}

function injectWebPrintToolbar(doc: Document, widthMm: ReceiptWidthMm): void {
  const ticket = doc.createElement('div');
  ticket.id = 'vuelto-ticket-sheet';
  while (doc.body.firstChild) {
    ticket.appendChild(doc.body.firstChild);
  }

  const toolbar = doc.createElement('div');
  toolbar.id = 'vuelto-print-toolbar';

  const hint = doc.createElement('p');
  const cutTip =
    widthMm === 72
      ? 'Tip: si se corta, el driver tiene que estar en 72 mm (no 58).'
      : `Tip: si se corta, el driver tiene que estar en ${widthMm} mm.`;
  hint.setAttribute('style', 'margin:0 0 8px;color:#374151;font-size:12px;line-height:1.45');
  hint.innerHTML = [
    'Tocá Imprimir. ¿PDF? Destino → Guardar como PDF.',
    `<span style="color:#6b7280;font-size:11px">${cutTip}</span>`,
  ].join('<br>');

  const row = doc.createElement('div');
  row.setAttribute('style', 'display:flex;gap:8px;flex-wrap:wrap');

  const printBtn = doc.createElement('button');
  printBtn.type = 'button';
  printBtn.textContent = 'Imprimir';
  printBtn.setAttribute('style', buttonStyle(true));
  printBtn.addEventListener('click', () => {
    winPrint(doc.defaultView);
  });

  row.appendChild(printBtn);
  toolbar.appendChild(hint);
  toolbar.appendChild(row);

  doc.body.appendChild(toolbar);
  doc.body.appendChild(ticket);
}

function centerPreviewWindow(win: Window): void {
  try {
    const left = Math.max(0, Math.round((window.screen.availWidth - win.outerWidth) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - win.outerHeight) / 2));
    win.moveTo(left, top);
  } catch {
    /* algunos browsers restringen moveTo */
  }
}

function fitPreviewWindow(win: Window, widthMm: ReceiptWidthMm): void {
  const doc = win.document;
  const ticketPx = mmToPx(widthMm);
  const contentWidth = Math.max(ticketPx + 48, TOOLBAR_MIN_WIDTH_PX);

  const applySize = () => {
    const chromeH = Math.max(win.outerHeight - win.innerHeight, 0);
    const chromeW = Math.max(win.outerWidth - win.innerWidth, 0);
    const contentHeight = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
    const winWidth = Math.min(contentWidth + chromeW, window.screen.availWidth - 16);
    const winHeight = Math.min(
      contentHeight + chromeH + 2,
      window.screen.availHeight - 16,
    );

    try {
      win.resizeTo(winWidth, Math.max(winHeight, 240));
      centerPreviewWindow(win);
    } catch {
      /* algunos browsers restringen resizeTo / moveTo */
    }
  };

  applySize();
  win.requestAnimationFrame(() => {
    applySize();
  });
}

function winPrint(view: Window | null): void {
  if (!view) return;
  try {
    view.focus();
    view.print();
  } catch {
    /* ignore */
  }
}

function openPrintPreviewWindow(html: string, widthMm: ReceiptWidthMm): Window {
  const ticketPx = mmToPx(widthMm);
  const initialWidth = Math.max(ticketPx + 48, TOOLBAR_MIN_WIDTH_PX);
  const initialHeight = 420;
  const initialLeft = Math.max(0, Math.round((window.screen.availWidth - initialWidth) / 2));
  const initialTop = Math.max(0, Math.round((window.screen.availHeight - initialHeight) / 2));
  const win = window.open(
    '',
    '_blank',
    `width=${initialWidth},height=${initialHeight},left=${initialLeft},top=${initialTop}`,
  );
  if (!win) {
    throw new Error(
      'El navegador bloqueó la ventana de impresión. Permití popups e intentá de nuevo.',
    );
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  return win;
}

/**
 * Print an HTML document.
 * Native: expo-print uses the provided HTML.
 * Web: preview del mismo HTML; Imprimir = window.print() (PDF vectorial vía Destino → Guardar como PDF).
 */
export async function printHtmlDocument(
  html: string,
  options?: PrintHtmlDocumentOptions,
): Promise<void> {
  if (Platform.OS !== 'web') {
    await Print.printAsync({ html });
    return;
  }

  if (typeof window === 'undefined') {
    throw new Error('Impresión no disponible');
  }

  const plainText = options?.plainText?.trim();
  const widthMm = options?.widthMm ?? DEFAULT_TICKET_WIDTH_MM;

  const win = openPrintPreviewWindow(html, widthMm);

  await new Promise<void>((resolve, reject) => {
    const onReady = () => {
      try {
        if (plainText) {
          win.document.title = receiptWindowTitle(plainText);
          injectPreviewChromeStyles(win.document, widthMm);
          injectWebPrintToolbar(win.document, widthMm);
          fitPreviewWindow(win, widthMm);
          win.focus();
          if (options?.autoOpenPrintDialog) {
            setTimeout(() => {
              try {
                winPrint(win);
              } catch {
                /* popup blocked o usuario canceló */
              }
            }, 150);
          }
          resolve();
          return;
        }

        win.document.title = 'Imprimir · Vuelto';
        fitPreviewWindow(win, widthMm);
        win.focus();
        win.addEventListener('afterprint', () => {
          try {
            win.close();
          } catch {
            /* ignore */
          }
        });
        setTimeout(() => {
          try {
            win.close();
          } catch {
            /* ignore */
          }
        }, 120_000);
        winPrint(win);
        resolve();
      } catch (e) {
        try {
          win.close();
        } catch {
          /* ignore */
        }
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };

    if (win.document.readyState === 'complete') {
      onReady();
    } else {
      win.onload = () => onReady();
    }
  });
}
